import { api } from './api';
import { toast } from 'sonner';

/**
 * Abre YA una pestaña en blanco, dentro del clic del usuario, para rellenarla
 * cuando llegue la URL (2026-08-05).
 *
 * Por qué: si se hace al revés —esperar al servidor y DESPUÉS `window.open`—
 * el navegador ya no lo considera parte del clic y lo bloquea sin avisar. Ese
 * era el "pulso y no pasa nada" de los botones de WhatsApp.
 */
export function abrirPestanaYa() {
  try {
    return window.open('about:blank', '_blank');
  } catch {
    return null;
  }
}

/**
 * Lleva la pestaña ya abierta a su destino. Si el navegador la bloqueó, deja
 * un aviso con un botón: pulsarlo SÍ es un gesto del usuario y abre bien.
 */
export function llevarPestanaA(ventana, url, textoAviso = 'Abrir WhatsApp') {
  if (ventana && !ventana.closed) {
    ventana.location.href = url;
    return;
  }
  toast('El navegador ha bloqueado la ventana', {
    description: 'Pulsa aquí para abrirlo.',
    duration: Infinity,
    closeButton: true,
    className: 'cjo-toast-confirmacion',
    action: { label: textoAviso, onClick: () => window.open(url, '_blank', 'noopener') },
  });
}

/**
 * Mensaje de WhatsApp asistido de una cita. `tipo`:
 *  - 'confirmacion': al agendar — "su cita ha quedado registrada".
 *  - 'recordatorio': el día antes — "le recordamos su cita".
 */
export async function mensajeCita(citaId, tipo = 'recordatorio') {
  const res = await api.get(`/api/recordatorios/cita/${citaId}`, { params: { tipo } });
  return res.data;
}

/**
 * Deja constancia en la cita de que el WhatsApp ha salido (lo que el panel
 * Recordatorios pinta como «Enviado por WhatsApp»). Lo llaman el panel y, desde
 * el cierre 2026-09-05, también el botón Confirmación/Recordatorio del
 * resumen de la cita. La respuesta trae la marca tal como la ha guardado el
 * servidor (`recordatorio_enviado_at`, `recordatorio_canal`; dirección,
 * 2026-09-08, punto 14): quien llama pinta con ESO, no con la hora del
 * navegador ni con una recarga que puede adelantarse a la marca. Si falla,
 * quien llama avisa: la cita sigue pendiente de verdad.
 */
export function marcarWhatsappEnviado(citaId) {
  return api.post(`/api/recordatorios/${citaId}/whatsapp-enviado`);
}

/**
 * Cómo se nombra el canal en la etiqueta «Enviado por …» (punto 14, dirección:
 * «la etiqueta "Enviado por WhatsApp"»): antes salía el campo tal cual,
 * «Enviado (whatsapp)».
 */
const NOMBRE_CANAL = { whatsapp: 'WhatsApp', email: 'email' };
export function nombreCanal(canal) {
  return NOMBRE_CANAL[canal] || canal || '';
}

/**
 * Aviso al equipo cuando el saludo del mensaje sale «Estimado/a» (el backend
 * lo dice con `saludo_neutro`, cierre 2026-09-05): ni la ficha ni el nombre
 * deciden el género. No bloquea el envío; se pide revisar la ficha.
 */
export function avisarSaludoNeutro(nombre = '') {
  toast.warning('El saludo sale como «Estimado/a»: revisa el sexo o el nombre en la ficha', {
    description: nombre || undefined,
    duration: 10000,
  });
}

/**
 * Confirmación de un RETOQUE sin coste conocido (dirección, 2026-09-08, punto
 * 8): el servidor busca el coste del retoque en la cita, los episodios y la
 * historia (`retoque_coste` en la respuesta de `mensajeCita`); si no lo
 * encuentra, el mensaje sale con el bloque «Gastos de quirófano» y aquí se
 * avisa, sin frenar el envío, para que recepción lo revise antes de mandar.
 */
export const AVISO_COSTE_RETOQUE = 'No se ha encontrado el coste del retoque en la historia: revisa antes de enviar';

/** Cita de retoque = su tipo de cita lleva la marca «es_retoque» en el
 * catálogo (Gestión de agendas), se escriba el código como se escriba: la
 * misma regla que services/cita_conceptos.es_retoque en el backend. Sin el
 * catálogo, o sin la marca, no es un retoque. */
export function esConceptoRetoque(code, conceptos = []) {
  const slug = String(code || '').trim().replace(/\s+/g, '-').toLowerCase();
  if (!slug) return false;
  const concepto = (conceptos || []).find(
    (c) => String(c?.code || '').trim().replace(/\s+/g, '-').toLowerCase() === slug);
  return !!concepto?.es_retoque;
}

/**
 * Al DAR una cita en quirófano (aún no existe, así que el servidor no la ha
 * podido marcar): ¿su tipo es una intervención? Por la categoría «cirugía» del
 * catálogo o por sus MARCAS —intervención, retoque o chalazión—, la misma
 * lectura que el servidor (`cita_conceptos.es_cirugia_o_retoque`), nunca por
 * el código. Un tipo que no está en la lista cuenta como intervención, como
 * en el servidor: lo que no se sabe no se inventa. Una revisión o una
 * consulta previa citadas en quirófano no lo son (2/10/2026): no se les pide
 * la intervención (services/estados_del_dia.modo_de_la_cita).
 */
export function conceptoEsIntervencion(code, conceptos = []) {
  const slug = String(code || '').trim().replace(/\s+/g, '-').toLowerCase();
  const concepto = (conceptos || []).find(
    (c) => String(c?.code || '').trim().replace(/\s+/g, '-').toLowerCase() === slug);
  if (!slug || !concepto) return true;
  // Un tipo que «lee la nota» citado en quirófano es una intervención (como
  // en el servidor, `estados_del_dia.modo_de_la_cita`): se pide la intervención.
  return String(concepto.category || '').trim().toLowerCase() === 'cirugia'
    || !!concepto.es_intervencion || !!concepto.es_retoque || !!concepto.es_chalazion
    || !!concepto.lee_la_nota;
}

export function costeRetoqueDesconocido(respuesta) {
  return !!respuesta?.retoque_coste && respuesta.retoque_coste.con_coste === null;
}

export function avisarCosteRetoque(nombre = '') {
  toast.warning(AVISO_COSTE_RETOQUE, {
    description: nombre || undefined,
    duration: 12000,
  });
}

/**
 * El mismo mensaje que `mensajeCita`, para VARIAS citas de golpe (panel
 * Recordatorios de la vista del día, 2026-09-03, dirección, punto 3). Devuelve
 * {canal: 'api'|'asistido', resultados: [{cita_id, estado, …}]}: con la API
 * de Meta el servidor ya los ha mandado ('enviado'); sin ella vuelven
 * 'preparado' con su whatsapp_url para abrirlos uno a uno.
 *
 * `reenviar` solo cuando recepción ha marcado a mano una fila ya enviada:
 * sin él, el servidor omite las que ya tienen recordatorio («Ya enviado»),
 * para que un segundo clic no reenvíe a todo el día (verificación de
 * cierre 2026-09-05).
 */
export async function enviarMensajesMasivo(citaIds, tipo, reenviar = false) {
  const res = await api.post('/api/recordatorios/enviar-masivo', {
    cita_ids: citaIds, tipo, ...(reenviar ? { reenviar: true } : {}),
  });
  return res.data;
}

/**
 * Tras crear una cita: ofrece en un toast mandar el mensaje de CONFIRMACIÓN
 * por WhatsApp (distinto del recordatorio del día anterior). Best-effort: si
 * el paciente no tiene teléfono no se ofrece nada.
 */
export function ofrecerConfirmacionWhatsApp(citaId) {
  if (!citaId) return;
  mensajeCita(citaId, 'confirmacion')
    .then((d) => {
      if (!d?.whatsapp_url) return;
      // Aviso GRANDE y que no se va solo (2026-08-05, dirección): antes duraba
      // 12 s y se escapaba en mitad de una consulta. Ahora hay que cerrarlo a
      // mano, y si se cierra sin enviar, el botón "Confirmación" del resumen
      // de la cita lo manda igual — no se pierde.
      toast.success('Cita creada · mándale la confirmación', {
        description: 'Si ahora no puedes, la tienes en el resumen de la cita → botón «Confirmación».',
        duration: Infinity,
        closeButton: true,
        className: 'cjo-toast-confirmacion',
        action: {
          label: 'Enviar por WhatsApp',
          onClick: () => window.open(d.whatsapp_url, '_blank', 'noopener'),
        },
      });
    })
    .catch(() => {});
}

// -----------------------------------------------------------------------------
// Phase 27 — Sistema de citas (agendas, conceptos, citas, bloqueos, episodios
// y sesiones de quirófano).
// -----------------------------------------------------------------------------
// Capa fina sobre `api` (axios con cookie httpOnly): cada función devuelve
// directamente `response.data` para que las páginas no repitan `.data` por
// todas partes. Los errores se propagan tal cual (axios) para que el caller
// pueda leer `err.response.data.detail` / `err.response.data.conflicts`.
// -----------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Agendas
// ---------------------------------------------------------------------------

/**
 * Lista de agendas visibles para el usuario, ya filtrada por rol y ORDENADA
 * por el servidor: consecutiva por profesional (su consulta, después su
 * quirófano, luego el siguiente médico), con las del propio usuario delante
 * (administración, 5/10/2026; la regla, en backend/services/orden_agendas.py).
 * Las píldoras, Gestión de agendas y los selectores la pintan tal cual llega:
 * nadie la reordena aquí, para que no haya dos reglas.
 */
export async function listAgendas() {
  const res = await api.get('/api/agendas');
  return res.data;
}


/** Solo admin. payload: {name?, color?, slot_minutes?, working_hours?, concept_overrides?,
 *  marca_clave?, marca_bloqueada?, doctor_user_id?, doctor_name?} */
export async function updateAgenda(agendaId, payload) {
  const res = await api.put(`/api/agendas/${agendaId}`, payload);
  return res.data;
}

/**
 * «Horario revisado (es correcto así)» (solo admin, 15/10/2026): la agenda abre
 * de verdad de lunes a viernes y deja de avisar (`horario_por_revisar`, que
 * calcula el servidor en cada agenda). Devuelve la agenda actualizada.
 */
export async function marcarHorarioRevisado(agendaId) {
  const res = await api.post(`/api/agendas/${agendaId}/horario-revisado`);
  return res.data;
}

/** Enlace a Gestión de agendas con «Configurar» abierto en esa agenda. */
export const enlaceConfigurarAgenda = (agendaId) => (
  `/gestion-agendas?configurar=${encodeURIComponent(agendaId)}`);

/**
 * Alta de agenda (solo admin, 30/09/2026). payload: {name, tipo, color,
 * doctor_user_id|null, doctor_name, copiar_de|null, consulta_conjunta,
 * marca_clave, marca_bloqueada}. Devuelve la agenda creada.
 */
export async function createAgenda(payload) {
  const res = await api.post('/api/agendas', payload);
  return res.data;
}

// ---------------------------------------------------------------------------
// Bloqueos de agenda
// ---------------------------------------------------------------------------

export async function listBloqueos(agendaId, { dateFrom, dateTo } = {}) {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await api.get(`/api/agendas/${agendaId}/bloqueos`, { params });
  return res.data;
}

/** Solo admin. payload: {date_from, date_to, all_day, start?, end?, reason?} */
export async function createBloqueo(agendaId, payload) {
  // agenda_id también en el body (el backend usa el del path, pero así la
  // petición es autosuficiente y compatible con el modelo).
  const res = await api.post(`/api/agendas/${agendaId}/bloqueos`, { agenda_id: agendaId, ...payload });
  return res.data;
}

/** Solo admin. */
/**
 * Quita un bloqueo o, con `fecha`, solo ese día de su tramo (el servidor lo
 * recorta o lo parte en dos; 409 si otra persona lo ha cambiado entre medias).
 * El de un patrón de bloqueo no se quita por aquí (409): se quita en Agendas
 * disponibles.
 */
export async function deleteBloqueo(bloqueoId, { fecha } = {}) {
  const res = fecha
    ? await api.delete(`/api/agendas/bloqueos/${bloqueoId}`, { params: { fecha } })
    : await api.delete(`/api/agendas/bloqueos/${bloqueoId}`);
  return res.data;
}

/** El bloqueo lo gestiona «Agendas disponibles» (vacaciones, días libres o
 *  un patrón de bloqueo): en otras pantallas, solo se mira. */
export function esDeAgendasDisponibles(b) {
  return !!(b && (b.patron_id || b.origen === 'agendas_disponibles'));
}

// ---------------------------------------------------------------------------
// Día de quirófano — el lugar (y cirujano en externos) es de TODO el
// quirófano de ese día; las citas lo heredan.
// ---------------------------------------------------------------------------

/** Config del día ({} si aún no está fijada). */
export async function getQuirofanoDia(agendaId, date) {
  const res = await api.get(`/api/agendas/${agendaId}/dia-quirofano`, { params: { date } });
  return res.data;
}

/** Fija o cambia lugar/cirujano/anestesista del día; se propaga a las citas
 *  existentes. El médico visitante ya no va aquí (6/10/2026): ver abajo. */
export async function setQuirofanoDia(agendaId, { date, lugar, cirujano, anestesista }) {
  const res = await api.put(`/api/agendas/${agendaId}/dia-quirofano`, { date, lugar, cirujano, anestesista });
  return res.data;
}

// ---------------------------------------------------------------------------
// Médico visitante (administración, 6/10/2026): de CUALQUIER agenda, consulta
// o quirófano, y de un día; hasta 2 a la vez, cada uno con nombre y
// especialidad. Es del día, no de las citas.
// ---------------------------------------------------------------------------

/** {agenda_id, fecha, medicos: [{nombre, especialidad}], max, …} */
export async function getMedicosVisitantes(agendaId, fecha) {
  const res = await api.get(`/api/agendas/${agendaId}/medicos-visitantes`, { params: { fecha } });
  return res.data;
}

/**
 * Guarda la lista ENTERA de ese día (reemplaza la que hubiera; [] los quita).
 * `actualizadoAt` es el de la lista de la que se parte (el que trajo el GET;
 * null si ese día no había lista guardada): si otra persona ha guardado
 * entretanto, el servidor contesta 409 y no pisa su cambio (6/10/2026).
 */
export async function setMedicosVisitantes(agendaId, fecha, medicos, actualizadoAt = null) {
  const res = await api.put(`/api/agendas/${agendaId}/medicos-visitantes`, {
    fecha, medicos, actualizado_at: actualizadoAt ?? null,
  });
  return res.data;
}

/**
 * Los médicos visitantes de VARIAS agendas un día, en una sola petición
 * (administración, 7/10/2026): {agenda_id: [{nombre, especialidad}]}, para la
 * vista de varias agendas a la vez. Solo las agendas que el usuario puede ver;
 * las demás no vienen. Solo lectura: se apuntan en la agenda suelta.
 */
export async function medicosVisitantesDelDia(fecha, agendaIds) {
  const res = await api.get('/api/agendas/medicos-visitantes-del-dia', {
    params: { fecha, agenda_ids: (agendaIds || []).join(',') },
  });
  const d = res.data;
  // Cualquier cosa que no sea {id: lista} se toma por «no lo sé».
  return d && typeof d === 'object' && !Array.isArray(d) ? d : null;
}

/**
 * Consulta conjunta: marcar/desmarcar una cita como vista por enfermería.
 * Desde el 15/10/2026 el resumen de la cita lo marca con el estado «Visto por
 * enfermería» (`setEstadoCita(id, 'visto_enfermeria')`), que pone esta misma
 * marca en el servidor; la ruta sigue para quien la use.
 */
export async function setVistoEnfermeria(citaId, visto = true) {
  const res = await api.post(`/api/citas/${citaId}/visto-enfermeria`, { visto });
  return res.data;
}

/** Flujo del día: null o un estado permitido por el tipo de agenda. */
export async function setEstadoCita(citaId, estado) {
  const res = await api.post(`/api/citas/${citaId}/estado`, { estado });
  return res.data;
}

/**
 * Etiquetas y colores del estado del paciente en el día. `corto` es lo que
 * dice el botón cuando la fila es larga (los cinco de consulta tienen que caber
 * en UNA línea del resumen de la cita, también en el móvil, 15/10/2026); el
 * `label` entero va en su `title`.
 */
export const ESTADOS_CITA = [
  { value: 'espera', label: 'Sala de espera', corto: 'Espera', color: '#D97706' },
  // Enfermería en el flujo de la consulta (administración, 15/10/2026):
  // «Enfermería» = el paciente está con enfermería; «Visto por enfermería» =
  // ha terminado con enfermería y espera para pasar a consulta. En una
  // consulta con consulta conjunta el visto pone además la marca de siempre
  // (check de la agenda, alarma), lo decide el servidor.
  { value: 'enfermeria', label: 'Enfermería', color: '#DB2777' },
  { value: 'visto_enfermeria', label: 'Visto por enfermería', corto: 'Visto enf.', color: '#0891B2' },
  { value: 'tratamiento', label: 'Consulta', color: '#2563EB' },
  { value: 'postop_ok', label: 'Listo para cambiarse', color: '#7C3AED' },
  { value: 'realizada', label: 'Realizada (en recuperación)', color: '#059669' },
  { value: 'se_fue', label: 'Se fue', color: '#64748B' },
  // Solo del chalazión citado en quirófano (dirección, 30/9/2026).
  { value: 'no_realizada', label: 'No realizado', color: '#A16207' },
];

/**
 * ¿Consulta ONLINE? El tipo de cita acaba en «-online», como decide el
 * servidor (services/cita_conceptos.py, `es_online`): la misma regla que el
 * parte impreso, el WhatsApp y la página de confirmar cita. Recortado, en
 * minúsculas y con los espacios como guiones, igual que `normalizar_code`.
 */
export function esOnline(code) {
  return String(code ?? '').trim().replace(/\s+/g, '-').toLowerCase().endsWith('-online');
}

/**
 * ¿Chalazión citado en quirófano? Lo dice el SERVIDOR en cada cita
 * (`chalazion_de_quirofano`, services/estados_del_dia.py), con las marcas del
 * tipo de cita: aquí no se decide por el código del concepto, igual que la
 * hoja de postoperatorio (22/9/2026).
 */
export function esChalazionDeQuirofano(cita) {
  return cita?.chalazion_de_quirofano === true;
}

/**
 * ¿Consulta citada en quirófano? Una revisión, una consulta previa… cuyo tipo
 * no es una intervención se comporta en todo como una cita de la agenda de
 * consultas (la clínica, 2/10/2026): sus estados son sala de espera → consulta
 * → realizada, recibe los mensajes de consulta y no lleva guía, CI ni hoja de
 * anestesia. Lo dice el SERVIDOR en cada cita (`consulta_en_quirofano`,
 * services/estados_del_dia.py), con las marcas del tipo de cita: aquí no se
 * decide nada por el código del concepto.
 */
export function esConsultaEnQuirofano(cita) {
  return cita?.consulta_en_quirofano === true;
}

/**
 * Estados que se ofrecen según la agenda (2026-07-28).
 * "Listo para cambiarse", "en recuperación" y "se fue" son de quirófano; en
 * consulta hay cinco pasos desde el 15/10/2026: sala de espera → enfermería →
 * visto por enfermería → consulta → realizada (`ESTADOS_DE_CONSULTA`).
 * El chalazión citado en quirófano (30/9/2026, con `cita`): "Listo para
 * cambiarse", "Realizado" y "No realizado", en lugar de "En recuperación" y
 * "Se fue". El servidor valida lo mismo, y su marca ya dice que la cita es
 * de quirófano (vale aunque la pantalla no tenga la agenda a mano).
 * Una consulta citada en quirófano (una revisión, 2/10/2026, `cita` con
 * `consulta_en_quirofano`): los de consulta, como en su agenda.
 */
export const ESTADOS_DE_CONSULTA = ['espera', 'enfermeria', 'visto_enfermeria', 'tratamiento', 'realizada'];

export function estadosDeAgenda(agenda, cita = null) {
  // Una consulta citada en quirófano (2/10/2026) lleva los de consulta.
  const esQuirofano = (agenda?.tipo || agenda?.type) === 'quirofano' && !esConsultaEnQuirofano(cita);
  if (esChalazionDeQuirofano(cita)) {
    return ESTADOS_CITA
      .filter((e) => ['postop_ok', 'realizada', 'no_realizada'].includes(e.value))
      .map((e) => (e.value === 'realizada' ? { ...e, label: 'Realizado' } : e));
  }
  if (esQuirofano) {
    return ESTADOS_CITA
      .filter((e) => ['postop_ok', 'realizada', 'se_fue'].includes(e.value))
      .map((e) => (e.value === 'realizada' ? { ...e, label: 'En recuperación' } : e));
  }
  return ESTADOS_CITA
    .filter((e) => ESTADOS_DE_CONSULTA.includes(e.value))
    .map((e) => (e.value === 'realizada' ? { ...e, label: 'Realizada' } : e));
}

/**
 * Los botones del flujo del día de ESTA cita con su estado guardado. Una cita
 * que guarda un estado que ya no se le ofrece —un chalazión de quirófano
 * marcado «Se fue» antes del 30/9/2026, una que dejó de ser chalazión con
 * «No realizado», o una revisión de quirófano marcada «Listo para cambiarse»
 * o «Se fue» antes del 2/10/2026— recibe su botón (encendido, `anterior`) para que se vea cómo
 * quedó y se pueda quitar (repetir el clic lo quita) o cambiar por uno de los
 * que tocan. Con un estado de la lista, exactamente como antes.
 */
export function estadosDeLaCita(agenda, cita, estadoActual) {
  const lista = estadosDeAgenda(agenda, cita);
  if (!estadoActual || lista.some((e) => e.value === estadoActual)) {
    return lista;
  }
  const suyo = ESTADOS_CITA.find((e) => e.value === estadoActual)
    || { value: estadoActual, label: estadoActual, color: '#64748B' };
  return [...lista, { ...suyo, anterior: true }];
}

/**
 * Qué avisar después de cambiar el estado, según lo que ha hecho el servidor
 * con el episodio automático de la ficha (cirugía o retoque desde el
 * 8/9/2026; chalazión de quirófano desde el 30/9/2026). Devuelve
 * [{tipo: 'success'|'warning', texto}].
 */
export function avisosDelEpisodio(respuesta, cita) {
  const chalazion = esChalazionDeQuirofano(respuesta) || esChalazionDeQuirofano(cita);
  const out = [];
  if (respuesta?.episodio_auto_creado) {
    out.push({
      tipo: 'success',
      texto: chalazion
        ? 'Episodio del chalazión creado en la ficha del paciente'
        : 'Episodio de la cirugía creado en la ficha del paciente',
    });
  }
  if (respuesta?.episodio_auto_actualizado) {
    out.push({ tipo: 'success', texto: 'Episodio de la ficha puesto al día con el nuevo estado' });
  }
  const sin = respuesta?.episodio_auto_sin_cambiar;
  if (sin) {
    out.push({
      tipo: 'warning',
      texto: `El episodio de la ficha no se ha cambiado (${sin.motivo || 'no se puede tocar'}): `
        + 'sigue diciendo lo del estado anterior. Corrígelo a mano en la historia clínica.',
    });
  }
  return out;
}

/**
 * ¿La agenda DE ESTA CITA tiene consulta conjunta (enfermería ve primero al
 * paciente)? Es lo que decide el check y el botón de «visto por enfermería».
 *
 * Se mira la agenda de la cita y no la que está abierta (24/9/2026): en la
 * agenda de Enfermería, las citas prestadas de Blefaroplastia se quedaban sin
 * el botón —justo el que usa enfermería— porque Enfermería no tiene consulta
 * conjunta. El servidor (`set_visto_enfermeria`) ya miraba la de la cita.
 *
 * `agendas`: las que tenga cargadas la pantalla (la abierta primero). Si la
 * de la cita no está entre ellas, vale lo que dice el listado de citas en
 * `consulta_conjunta_de_su_agenda`; sin ninguna de las dos cosas, no se
 * ofrece (el servidor lo rechazaría igual).
 */
export function consultaConjuntaDeLaCita(cita, agendas = []) {
  if (!cita) return false;
  const suya = (agendas || []).find((a) => a && a.id && a.id === cita.agenda_id);
  if (suya) return (suya.tipo || suya.type) !== 'quirofano' && !!suya.consulta_conjunta;
  return cita.consulta_conjunta_de_su_agenda === true;
}

/** Cita anulada: la rejilla de la agenda no la pinta. */
export function esCitaAnulada(cita) {
  return ['cancelada', 'cancelled'].includes(cita?.status);
}

/**
 * Las citas que van en la columna de UNA agenda cuando se ven varias a la vez:
 * las suyas y, en la agenda de enfermería, también los pacientes de su perfil
 * citados en otras consultas (2026-08-20). Cuáles lo decide el servidor en
 * `perfil_enfermeria`, no la pantalla: desde el 24/9/2026, solo los de las
 * consultas con consulta conjunta.
 *
 * Vive aquí desde el 5/10/2026 porque la usan dos cosas que tienen que contar
 * lo mismo: lo que se pinta en cada columna y el orden de las columnas.
 */
export function citasDeLaColumna(agenda, citas = []) {
  if (!agenda) return [];
  return (citas || []).filter((c) => c.agenda_id === agenda.id
    || (agenda.es_enfermeria && c.perfil_enfermeria && c.agenda_id !== agenda.id));
}

/**
 * ¿Pinta la columna de esta agenda alguna cita ese día? Es el «tener citas»
 * del orden de las columnas (abajo) y, desde el 5/10/2026, también el de
 * «Todas», que solo enseña las agendas con pacientes citados ese día
 * (administración: «las agendas vacías no deben aparecer en esa vista»).
 * Una sola regla para las dos cosas: sin las anuladas, con las «no acude» y,
 * en enfermería, con las prestadas (`citasDeLaColumna`). Que la «no acude»
 * no cuente como paciente (`pacientesCitados`) no la saca de la vista: sigue
 * en su hueco, tachada, y ahí hay algo que mirar.
 */
export function agendaTieneCitas(agenda, citas = [], fecha = null) {
  return citasDeLaColumna(agenda, citas)
    .some((c) => !esCitaAnulada(c) && (!fecha || c.date === fecha));
}

/**
 * Orden de las columnas con varias agendas a la vez (administración,
 * 5/10/2026): «las agendas que tengan citas agendadas ese día deben aparecer a
 * la izquierda, y las agendas vacías (sin citas) deben quedar al final, a la
 * derecha».
 *
 * «Tener citas» es tener alguna cita que se PINTA en su columna ese día: las
 * suyas (y, en enfermería, las prestadas), sin las anuladas, que la rejilla no
 * pinta. Una «no acude» sí cuenta: sigue en su hueco, tachada, y esa columna
 * no está vacía.
 *
 * Dentro de cada grupo, el orden de siempre: el de la lista de agendas tal y
 * como llega del servidor (por profesional desde el 5/10/2026, con las propias
 * delante: `listAgendas`). Es una partición estable: fuera de «con citas /
 * vacía», nada cambia de sitio, así que dentro de cada parte las agendas de un
 * mismo médico siguen seguidas y en su orden.
 * `fecha` (opcional): solo cuentan las citas de ese día.
 */
export function ordenarAgendasPorContenido(agendas = [], citas = [], fecha = null) {
  const conCitas = [];
  const vacias = [];
  for (const a of agendas || []) {
    (agendaTieneCitas(a, citas, fecha) ? conCitas : vacias).push(a);
  }
  return [...conCitas, ...vacias];
}

/**
 * Las columnas de «Todas» (administración, 14/10/2026), por este orden:
 *  1. las agendas con pacientes (`agendaTieneCitas`, la regla de siempre);
 *  2. las de doctores DISPONIBLES ese día, aunque no tengan pacientes;
 *  3. al final, las bloqueadas el día entero y las de doctores de vacaciones
 *     o con el día libre.
 * Las demás —doctor no disponible, sin pacientes y sin bloqueo— no salen.
 * Dentro de cada grupo, el orden de siempre (el de la lista de agendas).
 *
 * `estado`: {agenda_id: {estado}} de GET /api/agendas-disponibles/dia. Sin él
 * (no ha llegado o ha fallado) solo salen las de pacientes, como antes.
 */
export function columnasDeTodas(agendas = [], citas = [], fecha = null, estado = null) {
  const conCitas = [];
  const disponibles = [];
  const rojas = [];
  for (const a of agendas || []) {
    if (agendaTieneCitas(a, citas, fecha)) { conCitas.push(a); continue; }
    const e = estado?.[a.id]?.estado;
    if (e === 'disponible') disponibles.push(a);
    else if (e === 'bloqueada' || e === 'vacaciones' || e === 'dia_libre') rojas.push(a);
  }
  return [...conCitas, ...disponibles, ...rojas];
}

/** ¿Sale en «Todas» aunque no tenga pacientes? (grupos 2 y 3 de arriba). */
export function seVeEnTodasSinCitas(agenda, estado = null) {
  const e = estado?.[agenda?.id]?.estado;
  return e === 'disponible' || e === 'bloqueada' || e === 'vacaciones' || e === 'dia_libre';
}

/**
 * Pacientes citados ese día en cada agenda que ve este usuario,
 * {agenda_id: número}: lo que va junto al nombre de cada agenda en las
 * píldoras y en la cabecera de cada columna (administración, 5/10/2026).
 *
 * La regla la pone el SERVIDOR (`pacientes_por_agenda`, citas_router.py), la
 * misma que `pacientesCitados` (abajo) para el contador del día: personas y no
 * citas (el mismo paciente con dos citas ese día en la misma agenda cuenta una
 * vez), solo con cita activa —ni anuladas ni «no acude», dirección, 8/9/2026,
 * punto 12— y, en enfermería, con los de su perfil citados en otras
 * consultas. Hace falta pedirlo aparte porque con UNA agenda abierta la
 * pantalla solo carga las citas de esa, y las píldoras tienen que decir
 * cuántos tiene cada una sin entrar.
 *
 * Devuelve null si la respuesta no es un objeto (la pantalla no pinta números).
 */
export async function recuentoDelDia(fecha) {
  const res = await api.get('/api/citas/recuento-del-dia', { params: { fecha } });
  const datos = res?.data;
  return datos && typeof datos === 'object' && !Array.isArray(datos) ? datos : null;
}

/**
 * El mes de unas agendas, para la vista «Mes» (dirección, 14/10/2026), en
 * UNA petición: `desde` es el día 1 del mes (AAAA-MM-01). Por día y agenda,
 * si la mañana y la tarde están abiertas, cerradas o abiertas en parte, por
 * qué, y cuántos pacientes hay citados en cada una
 * ({desde, hasta, corte_tarde, agendas, dias, pacientes_por_dia}). La regla,
 * en backend/services/agenda_mes.py; solo vienen las agendas que el usuario
 * puede ver.
 *
 * Devuelve null si la respuesta no trae los días (la pantalla no pinta nada).
 */
export async function resumenDelMes(desde, agendaIds) {
  const res = await api.get('/api/citas/mes', {
    params: { desde, agendas: (agendaIds || []).join(',') },
  });
  const datos = res?.data;
  return datos && typeof datos === 'object' && datos.dias && typeof datos.dias === 'object'
    ? datos : null;
}

/**
 * Cuántas PERSONAS tienen cita activa en estas citas (ese día, si se da
 * `fecha`): el contador «N pacientes este día» de encima de la agenda
 * (revisión del 5/10/2026). Es la regla de `pacientes_por_agenda` en el
 * servidor, la del número de cada agenda, para que los dos números no se
 * contradigan: el mismo paciente con dos citas cuenta uno (por `patient_id`;
 * una cita sin paciente cuenta sola), y ni las anuladas ni las «no acude»
 * cuentan (dirección, 8/9/2026, punto 12).
 */
export function pacientesCitados(citas = [], fecha = null) {
  const quienes = new Set();
  for (const c of citas || []) {
    if (c?.status !== 'activa' || (fecha && c.date !== fecha)) continue;
    quienes.add(c.patient_id || `cita:${c.id}`);
  }
  return quienes.size;
}

// ---------------------------------------------------------------------------
// Notas internas de paciente — SOLO administración (el backend lo exige).
// ---------------------------------------------------------------------------

export async function getNotasInternas(patientId) {
  const res = await api.get(`/api/patients/${patientId}/notas-internas`);
  return res.data;
}

export async function setNotasInternas(patientId, texto) {
  const res = await api.put(`/api/patients/${patientId}/notas-internas`, { texto });
  return res.data;
}

/** Marcador CON/SIN IVA del tratamiento (Phase 27.21). iva: 'con' | 'sin'. */

/** Notas clínicas (Phase 27.20): privadas del autor + compartidas doctores/admin. */
export async function getNotasPrivadas(patientId) {
  const res = await api.get(`/api/patients/${patientId}/notas-privadas`);
  return res.data;
}
export async function setNotasPrivadas(patientId, texto) {
  const res = await api.put(`/api/patients/${patientId}/notas-privadas`, { texto });
  return res.data;
}
export async function getNotasCompartidas(patientId) {
  const res = await api.get(`/api/patients/${patientId}/notas-compartidas`);
  return res.data;
}
export async function setNotasCompartidas(patientId, texto) {
  const res = await api.put(`/api/patients/${patientId}/notas-compartidas`, { texto });
  return res.data;
}

// ---------------------------------------------------------------------------
// Conceptos de cita
// ---------------------------------------------------------------------------

/**
 * Tipos de cita. Sin opciones, solo los ACTIVOS (lo que ofrecen los
 * desplegables de alta). Con `{ todos: true }` también los retirados
 * (active=false) con su flag (revisión 2026-09-03): la agenda los necesita
 * para pintar con su color y nombre las citas ya dadas con un tipo retirado
 * («Cirugía» y «Cirugía menor» tras el punto 4), y Gestión de agendas para
 * poder reactivarlos.
 */
export async function listConceptos({ todos = false } = {}) {
  const res = await api.get('/api/cita-conceptos', todos ? { params: { todos: 1 } } : undefined);
  return res.data;
}

/** Los conceptos que se pueden ELEGIR en un desplegable: activos y, si la
 * cita ya lleva un tipo retirado, ese también (marcado) para que el
 * desplegable no quede en blanco. */
export function conceptosElegibles(conceptos = [], codeActual = null) {
  return conceptos.filter((c) => c.active !== false || c.code === codeActual);
}

/** Nombre del concepto para un desplegable: «Cirugía (retirado)» si ya no
 * se ofrece. */
export function nombreConcepto(concepto) {
  if (!concepto) return '';
  return concepto.active === false ? `${concepto.name} (retirado)` : concepto.name;
}

/** Solo admin. payload: {code (slug único), name, category, default_minutes, color?, sort_order?} */
export async function createConcepto(payload) {
  const res = await api.post('/api/cita-conceptos', payload);
  return res.data;
}

/** Solo admin. payload: {name?, category?, default_minutes?, color?, sort_order?} */
export async function updateConcepto(conceptoId, payload) {
  const res = await api.put(`/api/cita-conceptos/${conceptoId}`, payload);
  return res.data;
}

/** Solo admin. Soft-delete (active=false): las citas existentes no se ven afectadas. */
export async function deleteConcepto(conceptoId) {
  const res = await api.delete(`/api/cita-conceptos/${conceptoId}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Citas
// ---------------------------------------------------------------------------

/**
 * Lista citas. Combinaciones soportadas por el backend:
 *   { agendaId, date }                — día concreto de una agenda
 *   { agendaId, dateFrom, dateTo }    — rango de una agenda
 *   { patientId }                     — historial de un paciente
 * Añade `includeCancelled: true` para incluir canceladas.
 * Añade `includeNoAcude: true` para traer también las «no acude» (la agenda
 * las deja en su hueco con el nombre tachado; dirección, 2026-09-08, punto 12).
 */
export async function listCitas({ agendaId, date, dateFrom, dateTo, patientId, includeCancelled, includeNoAcude } = {}) {
  const params = {};
  if (agendaId) params.agenda_id = agendaId;
  if (date) params.date = date;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (patientId) params.patient_id = patientId;
  if (includeCancelled) params.include_cancelled = true;
  if (includeNoAcude) params.include_no_acude = true;
  const res = await api.get('/api/citas', { params });
  return res.data;
}

/**
 * Huecos libres → {slots: [{start, end}], duration_minutes}.
 * `durationMinutes` fuerza una duración distinta a la del concepto;
 * `excludeCitaId` ignora una cita existente (para mover/editar).
 * `patientId` (23/9/2026): no ofrece los huecos que dejarían a ese paciente en
 * dos sitios a la vez (una cita de quirófano); el servidor los rechazaría.
 */
export async function freeSlots({ agendaId, date, conceptCode, durationMinutes, excludeCitaId, patientId }) {
  const params = { agenda_id: agendaId, date, concept_code: conceptCode };
  if (durationMinutes) params.duration_minutes = durationMinutes;
  if (excludeCitaId) params.exclude_cita_id = excludeCitaId;
  if (patientId) params.patient_id = patientId;
  const res = await api.get('/api/citas/free-slots', { params });
  return res.data;
}

/**
 * Primer hueco libre a partir de hoy (o de dateFrom) →
 * {found, date, start, end, duration_minutes} o {found: false}.
 */
export async function primerHueco({ agendaId, conceptCode, durationMinutes, dateFrom, excludeCitaId, patientId }) {
  const params = { agenda_id: agendaId };
  if (conceptCode) params.concept_code = conceptCode;
  if (durationMinutes) params.duration_minutes = durationMinutes;
  if (dateFrom) params.date_from = dateFrom;
  if (excludeCitaId) params.exclude_cita_id = excludeCitaId;
  if (patientId) params.patient_id = patientId; // mismo criterio que freeSlots
  const res = await api.get('/api/citas/primer-hueco', { params });
  return res.data;
}

/**
 * payload: {agenda_id, patient_id, concept_code, date, start, notes?}
 * 409 si solapa → err.response.data = {detail, conflicts}.
 */
export async function createCita(payload) {
  const res = await api.post('/api/citas', payload);
  return res.data;
}

/** payload: {date?, start?, concept_code?, notes?, ...} — mover/editar. */
export async function updateCita(citaId, payload) {
  const res = await api.put(`/api/citas/${citaId}`, payload);
  return res.data;
}

/** Marca que el paciente no se presentó (queda en su histórico como «No acude»). */
export async function marcarNoAcude(citaId) {
  const { data } = await api.post(`/api/citas/${citaId}/no-acude`);
  return data;
}

/** Marcha atrás de un «no acude» marcado por error: la cita vuelve a activa. */
export async function deshacerNoAcude(citaId) {
  const { data } = await api.post(`/api/citas/${citaId}/no-acude/deshacer`);
  return data;
}

export async function cancelCita(citaId, reason = '') {
  const res = await api.post(`/api/citas/${citaId}/cancel`, { reason });
  return res.data;
}

// ---------------------------------------------------------------------------
// Episodios (historia evolutiva)
// ---------------------------------------------------------------------------

/** `patientId` es obligatorio (no hay listado global). */
export async function listEpisodios(patientId) {
  const res = await api.get('/api/episodios', { params: { patient_id: patientId } });
  return res.data;
}

/** Idempotente: devuelve el episodio abierto del doctor para paciente+fecha si ya existe. */
export async function createEpisodio(payload) {
  const res = await api.post('/api/episodios', payload);
  return res.data;
}

/**
 * Guarda el texto del evolutivo y devuelve el episodio ya guardado (con su
 * `updated_at` nuevo).
 *
 * `payload.visto_updated_at` es el sello de la versión que el editor tenía
 * delante. Desde 2026-09-17 el médico en consulta y un admin de mostrador
 * pueden estar en la misma nota a la vez, y este PUT manda el texto ENTERO:
 * sin el sello, el último en guardar borraba lo del otro en silencio. Si el
 * episodio ha cambiado por debajo el servidor NO escribe nada y contesta 409
 * con `{detail, conflicto: {texto, updated_at, quien, status}}` — el texto que
 * hay guardado ahora, para poder enseñar las dos versiones en vez de tragarse
 * la pérdida. El error sube tal cual (axios): lo atiende HistoriaClinicaTab.
 */
export async function updateEpisodio(episodioId, payload) {
  const res = await api.put(`/api/episodios/${episodioId}`, payload);
  return res.data;
}

export async function closeEpisodio(episodioId) {
  const res = await api.post(`/api/episodios/${episodioId}/close`);
  return res.data;
}


/**
 * Pasa el dictado a limpio con la IA, que aprende del propio doctor mirando
 * sus evolutivos anteriores. NO guarda: devuelve la propuesta para revisar.
 */
export async function pasarEpisodioALimpio(episodioId, texto) {
  const res = await api.post(`/api/episodios/${episodioId}/pasar-a-limpio`, { texto });
  return res.data; // {texto, ejemplos_usados}
}

/** Solo admin. */
/** Borra un episodio de la historia (solo quien tiene el permiso).
 *  Sin `aun_asi`, el servidor contesta 409 si arrastra presupuesto o actos de
 *  colaborador, para que se vea antes de decidir. */
export async function borrarEpisodio(episodioId, opciones = {}) {
  const res = await api.delete(`/api/episodios/${episodioId}`, { data: opciones });
  return res.data;
}

export async function reopenEpisodio(episodioId) {
  const res = await api.post(`/api/episodios/${episodioId}/reopen`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Sesiones de quirófano
// ---------------------------------------------------------------------------

export async function listSesionesQuirofano({ dateFrom, dateTo } = {}) {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  const res = await api.get('/api/quirofano-sesiones', { params });
  return res.data;
}

export async function createSesionQuirofano(payload) {
  const res = await api.post('/api/quirofano-sesiones', payload);
  return res.data;
}

export async function updateSesionQuirofano(sesionId, payload) {
  const res = await api.put(`/api/quirofano-sesiones/${sesionId}`, payload);
  return res.data;
}


// ---------------------------------------------------------------------------
// Helpers de tiempo/fecha (fechas de negocio SIEMPRE en local, "YYYY-MM-DD")
// ---------------------------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, '0');

/** "HH:MM" → minutos desde medianoche. toMin('09:30') === 570. */
export function toMin(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = String(hhmm).split(':');
  return (Number(h) || 0) * 60 + (Number(m) || 0);
}

/** minutos desde medianoche → "HH:MM". toHHMM(570) === '09:30'. */
export function toHHMM(min) {
  const total = Math.max(0, Math.round(Number(min) || 0));
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
}

/** Date → "YYYY-MM-DD" en hora local (NO usar toISOString: cambia de día por UTC). */
export function dateToStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** "YYYY-MM-DD" → Date local a medianoche. */
export function strToDate(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** Hoy en local, "YYYY-MM-DD". */
export function todayStr() {
  return dateToStr(new Date());
}

/** ¿Es "YYYY-MM-DD" y un día que existe? («2099-02-30», «20990101» o
 *  «2099-1-5», no.) Para fechas que llegan de fuera, como la dirección. */
export function esFechaValida(texto) {
  const s = String(texto || '');
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && dateToStr(strToDate(s)) === s;
}

/** «2026-10-14» → «martes 14 de octubre»: el día dentro de una frase, sin la
 *  coma de la fecha. Una sola para la Agenda (títulos de las agendas y
 *  etiquetas de la vista «Mes», 14/10/2026). */
export function diaEnFrase(dateStr) {
  return strToDate(dateStr).toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long',
  }).toLowerCase().replace(',', '');
}

/** Suma n días (puede ser negativo) a una fecha "YYYY-MM-DD". */
export function addDaysStr(dateStr, n) {
  const d = strToDate(dateStr);
  d.setDate(d.getDate() + n);
  return dateToStr(d);
}

// ---------------------------------------------------------------------------
// Constantes de dominio
// ---------------------------------------------------------------------------

/** Índice 0 = Lunes (mismo criterio que working_hours del backend). */
export const WEEKDAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export const SHIFT_LABELS = { manana: 'Mañana', tarde: 'Tarde' };

// Los lugares del quirófano y las clínicas del planificador ya no viven aquí
// (V2): son listas de la clínica, `lib/listasClinica.js`.

/** Color de un concepto con fallback corporativo. */
export function conceptColor(concepto) {
  return concepto?.color || '#2E6E5E';
}

// ---------------------------------------------------------------------------
// RESERVAS (administración 8/10; dirección, 12/10 y 15/10/2026). La cita
// guarda su tipo de siempre y la marca `reserva`; en el selector, junto a cada
// tipo con la marca «Admite reserva» sale «Reserva <tipo>». El estado lo
// decide el servidor (`estado_reserva`, services/reserva_cita.py) y se ve en
// el COLOR de la cita y en el nombre del servicio:
//   · «pendiente»: AMARILLO entero, la etiqueta RESERVA y «Reserva <tipo>»;
//   · «pagada_parcial» (cirugía con la reserva cobrada y el resto no): AZUL;
//   · «pagada_verde» (cirugía cubierta, o un pago único que deja verde): VERDE;
//   · «pagada» y «confirmada» (quitada a mano): el color de su tipo.
// Cómo se paga cada tipo (`reserva_modo`) y con qué cobros (`reserva_cobros`)
// lo configura la clínica en Gestión de agendas → Conceptos de cita.
// ---------------------------------------------------------------------------

/** El amarillo de la reserva sin pagar (el punto del selector). */
export const AMARILLO_RESERVA = '#FACC15';
/** El azul de la cirugía con la reserva pagada y el resto por cobrar. */
export const AZUL_RESERVA = '#3B82F6';
/** El verde de lo pagado del todo. */
export const VERDE_RESERVA = '#22C55E';
export const ESTADO_RESERVA = {
  PENDIENTE: 'pendiente',
  PAGADA_PARCIAL: 'pagada_parcial',
  PAGADA_VERDE: 'pagada_verde',
  PAGADA: 'pagada',
  CONFIRMADA: 'confirmada',
};
/**
 * El color del rectángulo de la cita según su reserva: fondo y borde, como
 * variables de CSS (index.css: claros en el modo claro y oscuros con buen
 * contraste en el oscuro), y el color del punto. null = el de su tipo.
 */
const COLOR_RESERVA = {
  [ESTADO_RESERVA.PENDIENTE]: { fondo: 'var(--cita-reserva-pendiente-fondo)', borde: 'var(--cita-reserva-pendiente-borde)', punto: AMARILLO_RESERVA },
  [ESTADO_RESERVA.PAGADA_PARCIAL]: { fondo: 'var(--cita-reserva-parcial-fondo)', borde: 'var(--cita-reserva-parcial-borde)', punto: AZUL_RESERVA },
  [ESTADO_RESERVA.PAGADA_VERDE]: { fondo: 'var(--cita-reserva-verde-fondo)', borde: 'var(--cita-reserva-verde-borde)', punto: VERDE_RESERVA },
};
export function colorDeLaReserva(estado) {
  return COLOR_RESERVA[estado] || null;
}

/** Cómo se paga la reserva de un tipo (`reserva_modo`, services/cita_conceptos). */
export const MODOS_RESERVA = [
  { valor: 'cirugia', etiqueta: 'Cirugía: amarilla → azul con la reserva → verde con el presupuesto cubierto', corto: 'cirugía' },
  { valor: 'pago_verde', etiqueta: 'Un pago la deja en verde', corto: 'pago → verde' },
  { valor: 'pago_color_propio', etiqueta: 'Un pago la deja en el color del tipo', corto: 'pago → su color' },
  { valor: 'manual', etiqueta: 'A mano: se elige el tipo normal', corto: 'a mano' },
];
export const MODO_RESERVA_POR_DEFECTO = 'manual';
export const MODOS_RESERVA_AUTOMATICOS = ['cirugia', 'pago_verde', 'pago_color_propio'];
const PREFIJO_RESERVA = 'reserva::';

/** ¿El tipo de cita lleva la marca «Admite reserva»? */
export function conceptoAdmiteReserva(concepto) {
  return !!concepto?.admite_reserva;
}

/**
 * Sin mayúsculas, sin tildes y sin espacios sobrantes: la misma regla que el
 * servidor (services/cita_conceptos.normalizar_texto_cobro) para casar el
 * concepto de un cobro con los textos que pagan una reserva.
 */
export function normalizarTextoCobro(texto) {
  return String(texto || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('es-ES')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
}

/** ¿El concepto del cobro CONTIENE alguno de los textos? Una devolución nunca. */
export function cobroPagaLaReserva(concepto, textos) {
  const c = normalizarTextoCobro(concepto);
  if (!c || c.startsWith('devolucion')) return false;
  return (textos || []).some((t) => {
    const n = normalizarTextoCobro(t);
    return n && c.includes(n);
  });
}

/** ¿La reserva de este tipo se paga sola con un cobro? (modo automático con textos) */
export function reservaSePagaSola(concepto) {
  return MODOS_RESERVA_AUTOMATICOS.includes(concepto?.reserva_modo)
    && (concepto?.reserva_cobros || []).length > 0;
}

/**
 * El servicio de la cita con la reserva sin pagar: «Reserva » + el nombre del
 * tipo con la primera letra en minúscula, salvo si las dos primeras son
 * mayúsculas (una sigla). «Cirugía X» → «Reserva cirugía X»; «AB1» →
 * «Reserva AB1». La misma regla que el servidor
 * (services/cita_conceptos.nombre_de_reserva).
 */
export function nombreReserva(nombre) {
  const n = String(nombre || '').trim().split(/\s+/).filter(Boolean).join(' ');
  if (!n) return 'Reserva';
  const esMayuscula = (ch) => !!ch && ch !== ch.toLocaleLowerCase('es-ES') && ch === ch.toLocaleUpperCase('es-ES');
  if (esMayuscula(n[0]) && esMayuscula(n[1])) return `Reserva ${n}`;
  return `Reserva ${n[0].toLocaleLowerCase('es-ES')}${n.slice(1)}`;
}

/** Los grupos del desplegable de tipo de cita, en su orden (21/10/2026). */
export const GRUPOS_TIPO_CITA = [
  { clave: 'consulta', etiqueta: 'Consultas' },
  { clave: 'cirugia', etiqueta: 'Cirugías' },
  { clave: 'reserva', etiqueta: 'Reservas' },
];

/**
 * Las opciones de un desplegable de tipo de cita, EN SU ORDEN (administración,
 * 21/10/2026, al pie de la letra): «primero todos los tipos que NO son
 * reserva, listados seguidos; después todos los que SÍ son reserva, listados
 * seguidos». Hasta entonces cada reserva iba justo detrás de su tipo.
 *
 *   1. las consultas (todo lo que no es de la categoría «cirugía»);
 *   2. las cirugías;
 *   3. las «Reserva <tipo>»: primero las de consulta y después las de
 *      cirugía, el mismo orden que sus tipos arriba.
 * Dentro de cada parte, el orden de la lista que se pasa: el `sort_order` con
 * que la manda el servidor (GET /api/cita-conceptos).
 *
 * La usan los tres sitios donde se elige el tipo (el alta desde la agenda,
 * «Dar cita» y el resumen de la cita): así no puede haber uno que ordene
 * distinto.
 *
 * Devuelve `[{ concepto, reserva, grupo }]`, con `grupo` la clave de
 * GRUPOS_TIPO_CITA. Opciones:
 *  - conReserva:    ofrecer las «Reserva <tipo>» (por defecto, sí);
 *  - ofreceReserva: (concepto) => si ese tipo ofrece la suya (por defecto, la
 *                   marca «Admite reserva» de Gestión de agendas).
 */
export function opcionesTipoCita(conceptos = [], { conReserva = true, ofreceReserva = conceptoAdmiteReserva } = {}) {
  const lista = (Array.isArray(conceptos) ? conceptos : []).filter(Boolean);
  const esCirugia = (c) => (c.category || 'consulta') === 'cirugia';
  const consultas = lista.filter((c) => !esCirugia(c));
  const cirugias = lista.filter(esCirugia);
  const normales = [
    ...consultas.map((concepto) => ({ concepto, reserva: false, grupo: 'consulta' })),
    ...cirugias.map((concepto) => ({ concepto, reserva: false, grupo: 'cirugia' })),
  ];
  const reservas = conReserva
    ? [...consultas, ...cirugias].filter((c) => ofreceReserva(c))
      .map((concepto) => ({ concepto, reserva: true, grupo: 'reserva' }))
    : [];
  return [...normales, ...reservas];
}

/** El valor de la opción del selector: el código, o «Reserva <tipo>». */
export function valorTipoCita(code, reserva = false) {
  if (!code) return '';
  return reserva ? `${PREFIJO_RESERVA}${code}` : code;
}

/** Al revés: qué tipo y si es la reserva. */
export function leerTipoCita(valor) {
  const v = String(valor || '');
  return v.startsWith(PREFIJO_RESERVA)
    ? { code: v.slice(PREFIJO_RESERVA.length), reserva: true }
    : { code: v, reserva: false };
}

/** ¿Tiene la cita la reserva SIN PAGAR? Manda lo que dice el servidor. */
export function reservaPendiente(cita) {
  if (!cita?.reserva) return false;
  if (cita.estado_reserva) return cita.estado_reserva === ESTADO_RESERVA.PENDIENTE;
  return !cita.reserva_cobro_id;
}

/** ¿Tiene la cita la reserva ya pagada (en azul, en verde o en su color)? */
export function reservaPagada(cita) {
  return !!cita?.reserva && !reservaPendiente(cita);
}

/** El nombre del tipo como se lee en la agenda: «Reserva <tipo>» mientras
 *  la reserva no esté pagada; pagada o confirmada, su tipo de siempre. */
export function nombreTipoDeLaCita(cita) {
  const nombre = cita?.concept_name || '';
  return reservaPendiente(cita) && nombre ? nombreReserva(nombre) : nombre;
}

/**
 * Minutos de un tipo de cita en una agenda: el override de la agenda si lo
 * hay, si no la duración por defecto del concepto (la misma regla que
 * resolve_duration en el backend). Es lo que se rellena solo en el campo
 * «Duración» al elegir el tipo de cita (2026-09-03, dirección). null si no
 * hay concepto o no tiene minutos.
 */
export function minutosConcepto(agenda, concepto) {
  if (!concepto) return null;
  return agenda?.concept_overrides?.[concepto.code] || concepto.default_minutes || null;
}

/** Lista de espera (Phase 27.18) — modo "quiere adelantar". */
export async function apuntarListaEspera(payload) {
  const res = await api.post('/api/lista-espera', payload); // {cita_id} o {patient_id, agenda_id, concept_code}
  return res.data;
}
export async function listarListaEspera(params = {}) {
  const res = await api.get('/api/lista-espera', { params });
  return res.data.items;
}
export async function huecosLiberados(params = {}) {
  const res = await api.get('/api/lista-espera/huecos-liberados', { params });
  return res.data.huecos;
}
export async function actualizarListaEspera(entradaId, payload) {
  const res = await api.put(`/api/lista-espera/${entradaId}`, payload);
  return res.data;
}
export async function borrarListaEspera(entradaId) {
  const res = await api.delete(`/api/lista-espera/${entradaId}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Hoja de anestesia DIGITAL (Registro de quirófano) — por paciente + fecha.
// ---------------------------------------------------------------------------
export async function listAnesthesiaRecords({ patientId, surgeryDate } = {}) {
  const params = {};
  if (patientId) params.patient_id = patientId;
  if (surgeryDate) params.surgery_date = surgeryDate;
  const res = await api.get('/api/anesthesia-records', { params });
  return res.data;
}
export async function upsertAnesthesiaRecord({ patientId, surgeryDate, citaId, agendaId, formData }) {
  const res = await api.post('/api/anesthesia-records', {
    patient_id: patientId,
    surgery_date: surgeryDate,
    cita_id: citaId || null,
    agenda_id: agendaId || null,
    form_data: formData || {},
  });
  return res.data;
}
export async function saveAnesthesiaRecord(recordId, formData, { signed } = {}) {
  const body = { form_data: formData };
  if (signed != null) body.signed = signed;
  const res = await api.put(`/api/anesthesia-records/${recordId}`, body);
  return res.data;
}

// ---------------------------------------------------------------------------
// Tiempo de cirugía (inicio/fin) apuntado en la vista de Quirófano. El informe
// «Registro de externos» ya no se lee de aquí: sale del Registro de quirófano
// (GET /api/registro-externos, dirección 2026-09-08, punto 6); estos tiempos le
// sirven de respaldo cuando la intervención del registro no lleva horas.
// ---------------------------------------------------------------------------
export async function setCirugiaTiempos(citaId, { inicio, fin }) {
  const res = await api.put(`/api/citas/${citaId}/cirugia-tiempos`, {
    inicio: inicio || null,
    fin: fin || null,
  });
  return res.data;
}
